import { useAtomValue,useSetAtom } from 'jotai';

import { InventoryDataSource,InventoryListingStatus } from '@ekhodealer/ekho-common';

import { type PatchInventoryItemInput,usePatchInventoryItem } from '../api/usePatchInventoryItem';
import {
	closeDrawerAtom,
	cosmeticFormTriggerAtom,
	cosmeticReportValidationErrorsAtom,
	drawerAtom,
	isPublishingAtom,
	isSavingAtom,
	isUnpublishingAtom,
	publishInventoryItemAtom,
	saveInventoryItemAtom,
	unpublishInventoryItemAtom,
	validationErrorsAtom,
} from '../atoms/editDrawerAtoms';
import { buildBackendUpdates, inventoryItemCanSave } from '../lib/utils';

/**
 * Custom hook that provides action handlers for the inventory drawer
 * Encapsulates the logic for save, publish, unpublish, and close actions
 */
export const useInventoryDrawerActions = () => {
	// Atom state
	const drawerState = useAtomValue(drawerAtom);
	const validationErrors = useAtomValue(validationErrorsAtom);
	const hasCosmeticErrors = useAtomValue(cosmeticReportValidationErrorsAtom);
	const triggerCosmeticValidation = useAtomValue(cosmeticFormTriggerAtom);
	const isPublishing = useAtomValue(isPublishingAtom);
	const isSaving = useAtomValue(isSavingAtom);
	const isUnpublishing = useAtomValue(isUnpublishingAtom);

	// State update atoms
	const closeDrawer = useSetAtom(closeDrawerAtom);
	const publishItem = useSetAtom(publishInventoryItemAtom);
	const unpublishItem = useSetAtom(unpublishInventoryItemAtom);
	const saveItem = useSetAtom(saveInventoryItemAtom);
	const setIsPublishing = useSetAtom(isPublishingAtom);
	const setIsSaving = useSetAtom(isSavingAtom);
	const setIsUnpublishing = useSetAtom(isUnpublishingAtom);

	// API mutations
	const { mutate: patchInventoryItem } = usePatchInventoryItem();

	/**
	 * Handle saving inventory item changes
	 * Validates data, builds API payload, and calls patch mutation with source=EKHO
	 */
	const handleSave = () => {
		// Validate before saving (button should be disabled anyway but just in case)
		if (!inventoryItemCanSave(drawerState, validationErrors)) {
			return;
		}

		// If blemishes have invalid data, surface inline RHF errors and bail.
		if (hasCosmeticErrors) {
			void triggerCosmeticValidation?.();
			return;
		}

		// Set loading state
		setIsSaving(true);

		// Build all updates including pricing fields
		const updates = buildBackendUpdates(drawerState) as PatchInventoryItemInput['updates'];

		// Call patch with source=EKHO for proper field attribution
		patchInventoryItem(
			{
				id: drawerState.selectedItemId!,
				updates: {
					...updates,
					source: InventoryDataSource.EKHO,
				},
			},
			{
				onSuccess: () => {
					saveItem(); // Reset hasUserMadeChanges flag
					setIsSaving(false);
				},
				onError: () => {
					setIsSaving(false);
				},
			},
		);
	};

	/**
	 * Handle publishing the inventory item
	 * This is effectively a save and publish operation
	 */
	const handlePublish = () => {
		// Validate before publishing (button should be disabled anyway but just in case)
		// Don't check for changes -- the user can publish an item without making any changes
		if (!inventoryItemCanSave(drawerState, validationErrors, true)) {
			return;
		}

		// If blemishes have invalid data, surface inline RHF errors and bail.
		if (hasCosmeticErrors) {
			void triggerCosmeticValidation?.();
			return;
		}

		// Set loading state
		setIsPublishing(true);

		// Build all updates including pricing fields
		const updates = buildBackendUpdates(drawerState) as PatchInventoryItemInput['updates'];

		// Call patch with publish status and source=EKHO
		patchInventoryItem(
			{
				id: drawerState.selectedItemId!,
				updates: {
					...updates,
					listing: {
						status: InventoryListingStatus.PUBLISHED,
					},
					source: InventoryDataSource.EKHO,
				},
			},
			{
				onSuccess: () => {
					publishItem(); // Set local state
					setIsPublishing(false);
				},
				onError: () => {
					setIsPublishing(false);
				},
			},
		);
	};

	/**
	 * Handle unpublishing the inventory item
	 * This is effectively a save and unpublish operation
	 */
	const handleUnpublish = () => {
		// Check if item ID exists
		if (!drawerState.selectedItemId) {
			return;
		}

		// Set loading state
		setIsUnpublishing(true);

		// Build all updates including pricing fields (only if there are changes)
		// For unpublish, we always want to change the status, even if there are validation errors
		const updates = buildBackendUpdates(drawerState) as PatchInventoryItemInput['updates'];

		// Call patch with hidden status and source=EKHO
		// Override any listing status from buildBackendUpdates with HIDDEN
		patchInventoryItem(
			{
				id: drawerState.selectedItemId,
				updates: {
					...updates,
					listing: {
						status: InventoryListingStatus.HIDDEN,
					},
					source: InventoryDataSource.EKHO,
				},
			},
			{
				onSuccess: () => {
					unpublishItem(); // Set local state
					setIsUnpublishing(false);
				},
				onError: () => {
					setIsUnpublishing(false);
				},
			},
		);
	};

	return {
		// Action handlers
		handleSave,
		handleClose: closeDrawer,
		handlePublish,
		handleUnpublish,

		// Loading states for UI feedback
		isSaving,
		isPublishing,
		isUnpublishing,
	};
};
